// Standalone SIP store - no external auth.
// Connects directly to the Asterisk WSS proxy with hardcoded WebRTC creds.
// All state is local. Logs to window.__sipLogs for the live log viewer.

import { create } from 'zustand'
import useUserStore from './userStore'

let JsSIP = null

const SIP_DOMAIN = import.meta.env.VITE_SIP_DOMAIN || 'agent.kailai.tech'
const SIP_WSS_URL = import.meta.env.VITE_SIP_WSS_URL || `wss://${SIP_DOMAIN}/ws`
// Default extension is overridden by useUserStore.user.extension at connect time
const SIP_EXTENSION = import.meta.env.VITE_SIP_EXTENSION || '1000'
const SIP_PASSWORD = import.meta.env.VITE_SIP_PASSWORD || 'test_webrtc_pwd_2026'

// In-memory log ring buffer + listeners
window.__sipLogs = window.__sipLogs || []
const logListeners = new Set()
function log(level, ...args) {
  const entry = {
    t: new Date().toISOString(),
    level,
    msg: args.map(a => typeof a === 'string' ? a : JSON.stringify(a)).join(' '),
  }
  window.__sipLogs.push(entry)
  if (window.__sipLogs.length > 500) window.__sipLogs.shift()
  console.log(`[sip:${level}]`, ...args)
  logListeners.forEach(fn => fn(entry))
}
export function subscribeLogs(fn) {
  logListeners.add(fn)
  return () => logListeners.delete(fn)
}
export function getLogs() {
  return [...window.__sipLogs]
}
export function clearLogs() {
  window.__sipLogs.length = 0
  logListeners.forEach(fn => fn(null))
}

function cleanCallerNumber(raw) {
  if (!raw) return '?'
  let num = String(raw)
  if (num.startsWith('sip:')) num = num.replace(/^sip:/, '').replace(/@.*$/, '')
  num = num.replace(/^\+/, '')
  if (num.startsWith('972') && num.length > 9) num = '0' + num.slice(3)
  return num || '?'
}

function buildTargetUri(phoneNumber) {
  const digits = String(phoneNumber).replace(/[^0-9]/g, '')
  let local = digits
  if (digits.startsWith('972')) local = '0' + digits.slice(3)
  return `sip:${local}@${SIP_DOMAIN}`
}

const useSipStore = create((set, get) => ({
  // Connection state
  ua: null,
  socket: null,
  session: null,
  remoteAudio: null,
  status: 'disconnected', // 'disconnected' | 'connecting' | 'registering' | 'registered' | 'failed'
  error: null,

  // Call state
  inCall: false,
  isRinging: false,
  isMuted: false,
  callDirection: null, // 'in' | 'out'
  callerNumber: null,
  callStartTime: null,

  // Stats
  registerCount: 0,
  lastRegisterError: null,

  // Configuration
  config: {
    domain: SIP_DOMAIN,
    wssUrl: SIP_WSS_URL,
    extension: SIP_EXTENSION,
    password: SIP_PASSWORD,
  },

  setConfig: (partial) => {
    set({ config: { ...get().config, ...partial } })
    log('info', 'Config updated', partial)
  },

  connect: async () => {
    const { status, config } = get()
    if (status === 'connecting' || status === 'registering' || status === 'registered') {
      log('warn', 'Already connecting/connected, status=' + status)
      return
    }

    // Pick extension + sip credentials from the logged-in user, fall back to config
    const user = useUserStore.getState().user
    const extension = user?.extension || config.extension
    const sipPassword = user?.sip_password || config.password
    const wssUrl = user?.ws_url || config.wssUrl
    const sipDomain = user?.sip_domain || config.domain

    set({ status: 'connecting', error: null })
    log('info', `connecting → ${wssUrl} as ${extension}@${sipDomain}`)

    try {
      if (!JsSIP) {
        log('info', 'Loading JsSIP module...')
        const mod = await import('jssip')
        JsSIP = mod.default || mod
        log('info', 'JsSIP loaded, version:', JsSIP.version || JsSIP.C?.USER_AGENT || '?')
      }

      // Audio element for incoming media
      let remoteAudio = document.getElementById('kaliai-remote-audio')
      if (!remoteAudio) {
        remoteAudio = document.createElement('audio')
        remoteAudio.id = 'kaliai-remote-audio'
        remoteAudio.autoplay = true
        document.body.appendChild(remoteAudio)
        log('info', 'Audio element created')
      }
      set({ remoteAudio })

      const socket = new JsSIP.WebSocketInterface(wssUrl)
      const ua = new JsSIP.UA({
        sockets: [socket],
        uri: `sip:${extension}@${sipDomain}`,
        password: sipPassword,
        register: true,
        register_expires: 600,
        session_timers: false,
        user_agent: 'KaliDialer/1.0',
        // WebRTC needs ICE/STUN/TURN for NAT traversal.
        // STUN alone won't work on symmetric NAT (most cellular networks);
        // TURN relays RTP through our server.
        pcConfig: {
          iceServers: [
            // Our own TURN server (also serves STUN for free)
            {
              urls: [
                `stun:${config.domain}:3478`,
                `turn:${config.domain}:3478?transport=udp`,
              ],
              username: 'kalidialer',
              credential: 'a8231bce7a612f6906557e683e39b1ba',
            },
            // Public STUN as fallback
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun.cloudflare.com:3478' },
          ],
          iceTransportPolicy: 'all',
          bundlePolicy: 'max-bundle',
        },
      })

      // Wire up all events with logging
      ua.on('connecting',          ()  => log('info', 'WS connecting...'))
      ua.on('connected',           ()  => log('info', '✓ WS connected'))
      ua.on('disconnected',        (e) => {
        log('warn', '✗ WS disconnected', e?.error ? `error=${e.error}` : '')
        set({ status: 'disconnected' })
      })
      ua.on('registered',          ()  => {
        log('info', '✓✓ REGISTERED')
        set({ status: 'registered', registerCount: get().registerCount + 1, lastRegisterError: null })
      })
      ua.on('unregistered',        ()  => {
        log('warn', 'Unregistered')
        set({ status: 'connecting' })
      })
      ua.on('registrationFailed',  (e) => {
        const cause = e.cause || 'unknown'
        const code = e.response?.status_code
        log('error', `✗ REGISTRATION FAILED: cause=${cause} code=${code || '?'} reason=${e.response?.reason_phrase || ''}`)
        set({ status: 'failed', error: `${cause}${code ? ` (${code})` : ''}`, lastRegisterError: cause })
      })

      ua.on('newRTCSession', (data) => {
        const session = data.session
        const direction = data.originator === 'remote' ? 'in' : 'out'
        const peer = cleanCallerNumber(session.remote_identity?.uri?.toString())
        log('info', `New ${direction === 'in' ? 'incoming' : 'outgoing'} session: ${peer}`)
        set({
          session,
          callerNumber: peer,
          callDirection: direction,
          isRinging: direction === 'in',
        })

        session.on('progress',  ()  => log('info', 'progress (ringing)'))
        session.on('accepted',  ()  => {
          log('info', '✓ call accepted')
          set({ inCall: true, isRinging: false, callStartTime: Date.now() })
        })
        session.on('confirmed', ()  => log('info', '✓ call confirmed'))
        log('info', '★★★ v3 audio diagnostics ★★★')

        // The peerconnection might already exist on outgoing sessions.
        // Try to grab it both immediately AND via the event.
        const attachToPc = (pc, source) => {
          log('info', `★ attaching to RTCPeerConnection from ${source}`)

          pc.addEventListener('iceconnectionstatechange', () => {
            log('info', `ICE state: ${pc.iceConnectionState}`)
          })
          pc.addEventListener('connectionstatechange', () => {
            log('info', `PC connection state: ${pc.connectionState}`)
          })
          pc.addEventListener('icegatheringstatechange', () => {
            log('info', `ICE gathering: ${pc.iceGatheringState}`)
          })
          pc.addEventListener('icecandidate', (evt) => {
            if (evt.candidate) {
              log('info', `ICE candidate (${evt.candidate.type}): ${evt.candidate.address || evt.candidate.candidate?.slice(0, 60)}`)
            } else {
              log('info', 'ICE gathering complete')
            }
          })

          pc.addEventListener('track', (evt) => {
            log('info', `★ received track: kind=${evt.track.kind} state=${evt.track.readyState} streams=${evt.streams?.length || 0}`)
            const ra = get().remoteAudio
            if (ra) {
              if (evt.streams?.[0]) {
                ra.srcObject = evt.streams[0]
                log('info', `★ audio attached via srcObject (${evt.streams[0].getTracks().length} tracks)`)
              } else {
                const ms = new MediaStream()
                ms.addTrack(evt.track)
                ra.srcObject = ms
                log('info', '★ audio attached via single-track MediaStream')
              }
              ra.play()
                .then(() => log('info', '✓ audio.play() succeeded'))
                .catch(err => log('error', `✗ audio.play() failed: ${err.message}`))
            } else {
              log('error', 'no audio element to attach to!')
            }
          })
        }

        // Method 1: peerconnection event (works for incoming, sometimes outgoing)
        session.on('peerconnection', (e) => {
          attachToPc(e.peerconnection, 'peerconnection event')
        })

        // Method 2: For outgoing sessions, the pc is created sync.
        // Poll briefly to catch it.
        let polls = 0
        const findPc = setInterval(() => {
          polls++
          if (session.connection) {
            clearInterval(findPc)
            attachToPc(session.connection, `polling (${polls}x)`)
          } else if (polls > 50) {
            // 5s timeout
            clearInterval(findPc)
            log('warn', 'never found session.connection (RTCPeerConnection)')
          }
        }, 100)
        session.on('ended', (e) => {
          log('info', `call ended: ${e.cause}`)
          set({ session: null, inCall: false, isRinging: false, isMuted: false,
                callerNumber: null, callDirection: null, callStartTime: null })
        })
        session.on('failed', (e) => {
          log('error', `call failed: cause=${e.cause} code=${e.message?.status_code || '?'}`)
          set({ session: null, inCall: false, isRinging: false,
                error: e.cause || 'Call failed',
                callerNumber: null, callDirection: null, callStartTime: null })
        })
      })

      set({ ua, socket, status: 'registering' })
      log('info', 'ua.start()')
      ua.start()
    } catch (e) {
      log('error', 'connect failed:', e.message)
      set({ status: 'failed', error: e.message })
    }
  },

  disconnect: () => {
    const ua = get().ua
    if (ua) {
      try { ua.stop() } catch (e) { log('warn', 'ua.stop error:', e.message) }
    }
    log('info', 'disconnected manually')
    set({ ua: null, socket: null, status: 'disconnected', error: null })
  },

  dial: (phoneNumber, options = {}) => {
    const { ua, status } = get()
    if (status !== 'registered') {
      log('error', `cannot dial - status=${status}`)
      throw new Error('Not registered')
    }
    const target = buildTargetUri(phoneNumber)
    log('info', `dialing ${target}${options.record ? ' (recording)' : ''}`)

    // Custom SIP headers - tell Asterisk dialplan to start MixMonitor
    const extraHeaders = []
    if (options.record) {
      extraHeaders.push('X-Record-Call: yes')
    }

    const session = ua.call(target, {
      mediaConstraints: { audio: true, video: false },
      sessionTimersExpires: 600,
      extraHeaders,
    })
    return session
  },

  // Record-by-default toggle, persisted to localStorage
  recordByDefault: (() => { try { return localStorage.getItem('kaliai_record') === '1' } catch { return false } })(),
  setRecordByDefault: (v) => {
    try { localStorage.setItem('kaliai_record', v ? '1' : '0') } catch {}
    set({ recordByDefault: !!v })
    log('info', `recording ${v ? 'enabled' : 'disabled'} for future calls`)
  },

  answer: () => {
    const session = get().session
    if (!session) return
    log('info', 'answering call')
    session.answer({ mediaConstraints: { audio: true, video: false } })
  },

  hangup: () => {
    const session = get().session
    if (!session) return
    log('info', 'hangup')
    try { session.terminate() } catch {}
  },

  setMuted: (muted) => {
    const session = get().session
    if (!session) return
    if (muted) session.mute({ audio: true })
    else session.unmute({ audio: true })
    set({ isMuted: muted })
    log('info', muted ? 'muted' : 'unmuted')
  },

  sendDtmf: (digit) => {
    const session = get().session
    if (!session) return
    try {
      session.sendDTMF(digit)
      log('info', `DTMF: ${digit}`)
    } catch (e) { log('warn', e.message) }
  },
}))

export default useSipStore
